import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import type { RoleFindInterface } from '@zaoblako/shared/modules/security/dtos/role/role-find.dto';
import { RoleFormDto, type RoleFormInterface } from '@zaoblako/shared/modules/security/dtos/role/role-form.dto';
import { RoleEnum } from '@zaoblako/shared/modules/security/enums/role.enum';
import { RoleEnumI18n } from '@zaoblako/shared/modules/security/i18n/role.enum.i18n';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { RoleService } from '@zaoblako/web/core/services/security/role.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-role-form',
  templateUrl: './role-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    SingleSelectComponent,
    JoinPipe,
    ButtonModule,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoleFormComponent extends BaseFormComponent<RoleFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public role: RoleFormInterface = {
    name: '',
    type: RoleEnum.ROLE_USER
  };

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public engine: 'class-transformer' | 'serde' = 'serde';
  public dto = RoleFormDto;

  public types: { name: string; value: RoleEnum }[] = Object.entries(RoleEnumI18n[I18nLocalesEnum.RU_RU]).map(
    ([value, name]) => ({
      name,
      value: value as RoleEnum
    })
  );

  public selectedType: { name: string; value: RoleEnum };

  public constructor(
    private readonly roleService: RoleService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.roleService.findById<RoleFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (role) => {
        this.role = role;
        this.selectedType = this.types.find((type) => type.value === this.role.type);
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveRole() {
    if (this.checkValidate(this.role).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.roleService.update<RoleFormInterface, RoleFindInterface>(this.role).subscribe({
          next: (role) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Роль "${this.role.name}" обновлена`);
            this.formStoreService.saveNext(role, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.roleService.create<RoleFindInterface>(this.role).subscribe({
          next: (role) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Роль создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/user/role/card/' + role.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(role, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteRole() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить роль "${this.role?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.roleService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Роль "${this.role?.name}" удалена`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public setType(data: { name: string; value: RoleEnum }) {
    this.role.type = data.value;
  }
}
